import { z } from 'zod';

/** Recordatorio: todo `...Pct` va de 0 a 100, nunca en tanto por uno. */

export const unitSchema = z.enum([
  'g', 'kg', 'lb', 'oz',
  'ml', 'cl', 'l', 'taza', 'cucharada', 'cucharadita',
  'pz', 'docena',
]);

/*
 * Formatos de compra. Antes solo kg/L/pz: un restaurante que compra en
 * libras (Mexico, EE.UU.) no podia dar de alta sus ingredientes tal cual.
 * Se mantiene fuera lo que solo tiene sentido recetando (taza, cucharada).
 */
export const purchaseUnitSchema = z.enum([
  'g', 'kg', 'lb', 'oz',
  'ml', 'cl', 'l',
  'pz', 'docena',
]);

export const unitSystemSchema = z.enum(['metrico', 'imperial']);

export const ingredientGroupSchema = z.enum([
  'lacteos_huevos', 'secos_granos', 'abarrotes', 'frutas_verduras', 'carnes_pescados',
  'vinos_licores', 'bebidas', 'aceites', 'limpieza', 'desechables', 'subrecetas', 'otros',
]);

export const expenseCategorySchema = z.enum([
  'compras_materia_prima', 'otros_insumos', 'sueldos_salarios', 'cuotas_ss', 'alquiler',
  'reparaciones', 'honorarios', 'seguros', 'comisiones_bancarias', 'uniformidad',
  'publicidad_rrss', 'comunicacion_web', 'suministros_agua', 'suministros_electricidad',
  'suministros_gas', 'servicios_varios', 'telefonia', 'papeleria', 'otros_gastos',
  'amortizaciones', 'intereses', 'impuesto_ganancias',
]);

export const allergenSchema = z.enum([
  'gluten', 'crustaceos', 'huevos', 'pescado', 'cacahuetes', 'soja', 'lacteos',
  'frutos_secos', 'apio', 'mostaza', 'sesamo', 'sulfitos', 'altramuces', 'moluscos',
]);

export const paymentMethodSchema = z.enum(['efectivo', 'tarjeta', 'otros']);
export const productTypeSchema = z.enum(['alimentos', 'bebidas', 'eventos', 'otros']);

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const ISO_MONTH_RE = /^(\d{4})-(\d{2})$/;

export function isIsoMonth(value: string): boolean {
  const match = ISO_MONTH_RE.exec(value);
  if (!match) return false;
  const month = Number(match[2]);
  return month >= 1 && month <= 12;
}

export function isIsoDate(value: string): boolean {
  const match = ISO_DATE_RE.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1) return false;
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return day <= days[month - 1];
}

export const isoDateSchema = z
  .string()
  .regex(ISO_DATE_RE, 'La fecha debe tener formato YYYY-MM-DD')
  .refine(isIsoDate, 'La fecha indicada no existe');
export const isoMonthSchema = z
  .string()
  .regex(ISO_MONTH_RE, 'El mes debe tener formato YYYY-MM')
  .refine(isIsoMonth, 'El mes indicado no existe');
const isoDate = isoDateSchema;
const isoMonth = isoMonthSchema;
const money = z.number().finite().min(0, 'El importe no puede ser negativo');
const pct = (max = 100) => z.number().min(0).max(max);
/** Merma del 100 % significaria que no queda producto */
const wastePct = z.number().min(0).lt(100, 'La merma debe ser menor del 100 %');

/* ------------------------------------------------------------------ */
/* Ajustes                                                             */
/* ------------------------------------------------------------------ */

export const deliveryPlatformSchema = z.object({
  platformId: z.string().min(1),
  name: z.string().trim().min(1).max(60),
  commissionPct: pct(90),
  active: z.boolean().default(true),
});

export const tenantSettingsSchema = z.object({
  vatPct: pct(),
  bankCommissionPct: pct(20),
  platforms: z.array(deliveryPlatformSchema).max(10).default([]),
  customIngredientGroups: z
    .array(z.string().trim().min(1).max(40))
    .max(30)
    .optional(),
  /** Subgrupos de gasto por grupo de Alex (solo los 3 de explotacion). */
  expenseSubgroups: z
    .object({
      materia_prima: z.array(z.string().trim().min(1).max(40)).max(30).optional(),
      personal: z.array(z.string().trim().min(1).max(40)).max(30).optional(),
      generales: z.array(z.string().trim().min(1).max(40)).max(30).optional(),
    })
    .optional(),
  /** Metrico (kg, L) o imperial (lb, oz). Solo afecta a lo que se ofrece. */
  unitSystem: unitSystemSchema.optional(),
  /*
   * Orden manual de la carta. Los topes son generosos pero existen: esto lo
   * escribe el navegador con la lista entera cada vez que se arrastra algo.
   */
  menuOrder: z
    .object({
      groups: z.array(z.string().trim().min(1).max(60)).max(60).optional(),
      items: z.array(z.string().min(1).max(64)).max(2_000).optional(),
    })
    .optional(),
  /** Grupos y categorias de serie que este restaurante no quiere ver. */
  hiddenIngredientGroups: z.array(z.string().trim().min(1).max(60)).max(30).optional(),
  hiddenExpenseCategories: z.array(z.string().trim().min(1).max(60)).max(30).optional(),
  /*
   * Avisos reconocidos. El tope es alto porque hay uno por plato y tipo de
   * aviso, pero existe: esto tambien lo escribe el navegador entero.
   */
  sharedCostAllocation: z
    .object({
      method: z.enum(['ventas', 'fijo']),
      shares: z.record(z.string().min(1).max(80), z.number().min(0).max(100)).optional(),
    })
    .optional(),
  alertAcks: z
    .array(
      z.object({
        key: z.string().min(1).max(120),
        value: z.number().finite().optional(),
        detail: z.string().max(400).optional(),
        at: z.string().min(1).max(40),
      }),
    )
    .max(2_000)
    .optional(),
  alerts: z.object({
    costIncreasePct: pct(),
    costDecreasePct: pct(),
    minGrossMarginPct: pct(),
  }),
});

/* ------------------------------------------------------------------ */
/* Costeo                                                              */
/* ------------------------------------------------------------------ */

export const ingredientInputSchema = z.object({
  name: z.string().trim().min(1, 'El nombre del producto es obligatorio').max(120),
  brand: z.string().trim().max(80).optional(),
  presentation: z.string().trim().max(120).optional(),
  // Grupo de serie o personalizado (los personalizados se validan contra
  // los ajustes del tenant en la capa de rutas, no aqui)
  group: z.string().trim().min(1).max(40).default('otros'),
  supplierId: z.string().optional(),
  purchaseQty: z.number().positive('El formato de compra debe ser mayor que 0').optional(),
  purchaseUnit: purchaseUnitSchema.optional(),
  /*
   * Un solo precio y siempre sin IVA: el precio con IVA es un calculo que se
   * hace al mostrarlo. Guardar los dos seria garantizarse que un dia dejan de
   * cuadrar. Quien tenga delante un tique con IVA lo teclea en la casilla de
   * "Precio con IVA" y el formulario manda ya el neto.
   */
  netPrice: z.number().positive('Introduce el precio de compra sin IVA').optional(),
  vatPct: pct().default(10),
  densityGPerMl: z.number().positive().optional(),
  createdVia: z.enum(['manual', 'importacion', 'alexia', 'factura']).optional(),
  priceSource: z.enum(['manual', 'importacion', 'factura', 'alexia']).optional(),
  active: z.boolean().default(true),
}).superRefine((value, ctx) => {
  const tieneFormato = value.purchaseQty != null || value.purchaseUnit != null;
  if (tieneFormato && (value.purchaseQty == null || value.purchaseUnit == null)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['purchaseQty'], message: 'Completa la cantidad y la unidad del formato de compra.' });
  }
  if (value.netPrice != null && !tieneFormato) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['purchaseQty'], message: 'Indica el formato de compra antes de guardar un precio.' });
  }
});

export const recipeComponentSchema = z
  .object({
    ref: z.discriminatedUnion('kind', [
      z.object({ kind: z.literal('ingredient'), ingredientId: z.string().min(1) }),
      z.object({ kind: z.literal('recipe'), recipeId: z.string().min(1) }),
    ]),
    block: z.enum(['ingrediente', 'empaque']).default('ingrediente'),
    qty: z.number().positive('La cantidad debe ser mayor que 0'),
    unit: unitSchema,
    wastePct: wastePct.default(0),
    note: z.string().max(200).optional(),
    estimated: z.boolean().optional(),
  })
  .transform((component) =>
    component.block === 'empaque' ? { ...component, wastePct: 0 } : component,
  );

export const recipeInputSchema = z.object({
  name: z.string().trim().min(1, 'El nombre de la receta es obligatorio').max(120),
  kind: z.enum(['plato', 'subreceta']).default('plato'),
  code: z.string().trim().max(20).optional(),
  steps: z.string().max(4_000).optional(),
  // data-URL comprimido (~200 KB) hoy; URL de S3 manana. 300k caracteres
  // deja margen bajo el limite de 400 KB por item de DynamoDB.
  photo: z.string().max(300_000).optional(),
  group: z.string().trim().max(60).optional(),
  allergens: z
    .array(allergenSchema)
    .max(14)
    .optional()
    .transform((list) => (list ? [...new Set(list)] : list)),
  components: z.array(recipeComponentSchema).default([]),
  yieldQty: z.number().positive('El rendimiento debe ser mayor que 0'),
  yieldUnit: unitSchema,
  portions: z.number().int().positive('Las porciones deben ser un entero positivo').default(1),
  safetyMarginPct: z.number().min(0).lt(100).default(10),
  useYieldCostAsBase: z.boolean().default(false),
  active: z.boolean().default(true),
  createdVia: z.enum(['manual', 'importacion', 'alexia']).optional(),
});

export const menuItemInputSchema = z.object({
  recipeId: z.string().min(1),
  name: z.string().trim().min(1).max(120),
  sellingPriceGross: money,
  vatPct: pct().default(10),
  targetFoodCostPct: z.number().gt(0).max(100).default(30),
  platformId: z.string().optional(),
  /** Grupo de carta del producto. Vacio = el de la receta (ver `menuGroupOf`). */
  group: z.string().trim().max(60).optional(),
  /**
   * PVP por canal: platformId → PVP con IVA (numero), o null si el plato no
   * se vende en ese canal. Sin entrada, el canal hereda el PVP de comedor.
   */
  channelPrices: z
    .record(z.string().min(1).max(60), money.nullable())
    .optional()
    .refine((value) => !value || Object.keys(value).length <= 20, {
      message: 'Demasiados canales: el máximo son 20.',
    }),
  active: z.boolean().default(true),
});

/** Campos que solo puede escribir el servidor o un endpoint especializado. */
export const menuItemInternalInputSchema = menuItemInputSchema.extend({
  theoreticalCost: money.optional(),
  theoreticalCostAt: z.string().optional(),
  unitsSold: z.number().int().min(0).optional(),
  unitsSoldByLocation: z.record(z.string().min(1).max(80), z.number().int().min(0)).optional(),
});

export const menuItemUnitsSchema = z.object({
  unitsSold: z.number().int().min(0),
  /** Local al que pertenecen las unidades. Ausente = total sin desglose. */
  locationId: z.string().trim().min(1).max(80).optional(),
});

/* ------------------------------------------------------------------ */
/* Inventario                                                          */
/* ------------------------------------------------------------------ */

/**
 * Recuento mensual. Solo viajan ingrediente y cantidad: el valor lo calcula
 * SIEMPRE el servidor con los precios vigentes, para que nadie pueda mandar
 * unas existencias infladas y maquillar la cuenta de resultados.
 */
export const inventoryCountInputSchema = z.object({
  locationId: z.string().trim().min(1).max(64).optional(),
  lines: z
    .array(
      z.object({
        ingredientId: z.string().min(1),
        qty: z.number().min(0).max(1_000_000),
      }),
    )
    .max(1_000, 'Demasiadas líneas de inventario.')
    .default([]),
});

export type InventoryCountInput = z.infer<typeof inventoryCountInputSchema>;

/* ------------------------------------------------------------------ */
/* Financiero                                                          */
/* ------------------------------------------------------------------ */

export const dailyRevenueInputSchema = z.object({
  locationId: z.string().optional(),
  date: isoDate,
  // Acepta también las claves antiguas (debito/credito) y las funde en
  // `tarjeta`: así re-guardar un día cargado antes de la unificación no falla.
  inHouse: z
    .object({
      efectivo: money.default(0),
      tarjeta: money.default(0),
      debito: money.default(0),
      credito: money.default(0),
      otros: money.default(0),
    })
    .partial()
    .default({})
    .transform((v) => ({
      efectivo: v.efectivo ?? 0,
      tarjeta: (v.tarjeta ?? 0) + (v.debito ?? 0) + (v.credito ?? 0),
      otros: v.otros ?? 0,
    })),
  delivery: z.record(z.string(), money).default({}),
  customers: z.number().int().min(0).optional(),
  tables: z.number().int().min(0).optional(),
  deliveryOrders: z.number().int().min(0).optional(),
  courtesies: money.optional(),
  discounts: money.optional(),
  notes: z.string().max(500).optional(),
});

export const expenseInputSchema = z
  .object({
    locationId: z.string().optional(),
    paymentType: z.enum(['contado', 'credito']).default('contado'),
    date: isoDate,
    dueDate: isoDate.optional(),
    paymentDate: isoDate.optional(),
    concept: z.string().trim().min(1, 'El concepto es obligatorio').max(160),
    supplierId: z.string().optional(),
    invoiceNumber: z.string().trim().max(40).optional(),
    documentType: z.enum(['factura', 'albaran']).optional(),
    category: expenseCategorySchema,
    attachmentKey: z.string().trim().max(200).optional(),
    subgroup: z.string().trim().min(1).max(40).optional(),
    nature: z.enum(['fijo', 'variable']),
    netAmount: money,
    vatPct: pct().default(21),
    paid: z.boolean().default(true),
    invoiceStatus: z.enum(['facturado', 'pendiente', 'sin_factura']).default('facturado'),
    notes: z.string().max(500).optional(),
  })
  .refine((v) => v.paymentType !== 'credito' || !!v.dueDate, {
    message: 'Un gasto a crédito necesita fecha de vencimiento',
    path: ['dueDate'],
  })
  .refine((v) => !v.dueDate || v.dueDate >= v.date, {
    message: 'El vencimiento no puede ser anterior a la fecha de emisión',
    path: ['dueDate'],
  });

export const investmentInputSchema = z.object({
  locationId: z.string().trim().min(1).max(80).optional(),
  date: isoDate,
  concept: z.string().trim().min(1).max(160),
  quantity: z.number().positive().default(1),
  unitPrice: money,
  vatPct: pct().default(21),
  usefulLifeYears: z.number().int().min(0).max(50).default(0),
  fundingSource: z.enum(['propio', 'prestamo', 'leasing', 'subvencion']).optional(),
  notes: z.string().max(500).optional(),
});

export const cashMovementInputSchema = z.object({
  locationId: z.string().trim().min(1).max(80).optional(),
  date: isoDate,
  kind: z.enum([
    'ingresos_inversiones', 'financiacion_aportes', 'otros_ingresos',
    'pago_impuestos', 'otros_gastos', 'ingresos_financieros', 'gastos_financieros',
  ]),
  concept: z.string().trim().min(1).max(160),
  amount: money,
  notes: z.string().max(500).optional(),
});

export const targetInputSchema = z.object({
  locationId: z.string().trim().min(1).max(80).optional(),
  month: isoMonth,
  netRevenue: money.optional(),
  averageTicket: money.optional(),
  averageTicketPerTable: money.optional(),
  materialPurchases: money.optional(),
  foodCostPct: pct().optional(),
  laborCostPct: pct().optional(),
  primeCostPct: pct().optional(),
  ebitdaPct: pct().optional(),
  safetyMarginPct: pct().optional(),
});

/** Importacion de gastos por archivo: hasta 2.000 filas por llamada. */
export const expenseBulkInputSchema = z.object({
  items: z.array(expenseInputSchema).min(1, 'No hay gastos que importar.').max(2_000),
});

export const taxAllocationInputSchema = z.object({
  payment: z.number().positive('El importe del pago debe ser mayor que 0'),
  months: z.array(isoMonth).min(1, 'Selecciona al menos un mes').max(12),
  base: z.enum(['facturacion', 'nomina']),
});

export const periodQuerySchema = z
  .object({ start: isoDate, end: isoDate })
  .refine((v) => v.start <= v.end, {
    message: 'La fecha de inicio no puede ser posterior a la de fin',
  });

export type IngredientInput = z.infer<typeof ingredientInputSchema>;
export type RecipeInput = z.infer<typeof recipeInputSchema>;
export type MenuItemInput = z.infer<typeof menuItemInputSchema>;
export type MenuItemInternalInput = z.infer<typeof menuItemInternalInputSchema>;
export type DailyRevenueInput = z.infer<typeof dailyRevenueInputSchema>;
export type ExpenseInput = z.infer<typeof expenseInputSchema>;
export type InvestmentInput = z.infer<typeof investmentInputSchema>;
export type CashMovementInput = z.infer<typeof cashMovementInputSchema>;
export type TargetInput = z.infer<typeof targetInputSchema>;
